import React from 'react';
import { Chip, Menu } from '@mantine/core';
import { IconCheck, IconChevronDown, IconEye, IconPencil, IconSend, IconX, type Icon } from '@tabler/icons-react';
import {
    accionTransicionEstado,
    colorEstadoCotizacion,
    EnumEstadoCotizacionNBS,
    nombreEstadoCotizacion,
    type EstadoCotizacionNBSId
} from '../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS';

const ICONOS_ESTADO: Record<number, Icon> = {
    [EnumEstadoCotizacionNBS.Borrador]: IconPencil,
    [EnumEstadoCotizacionNBS.RevisionInterna]: IconEye,
    [EnumEstadoCotizacionNBS.RevisionCliente]: IconSend,
    [EnumEstadoCotizacionNBS.Aceptada]: IconCheck,
    [EnumEstadoCotizacionNBS.Rechazada]: IconX,
};

interface BadgeEstadoCotizacionVistaProps {
    estadoId?: number;
    /** Estados a los que se puede pasar (los envía el back). Vacío = solo se muestra. */
    transiciones?: number[];
    alCambiarEstado?: (estado: EstadoCotizacionNBSId) => void;
    size?: 'xs' | 'sm';
}

/**
 * Chip del estado de la cotización, con el estilo estándar de estados de la app (Tareas, Contratos).
 * Si hay transiciones permitidas, al hacer clic despliega el menú para cambiar el estado
 * (se usa igual en la tabla y en el encabezado del formulario).
 */
export const BadgeEstadoCotizacionVista: React.FC<BadgeEstadoCotizacionVistaProps> = ({
    estadoId,
    transiciones = [],
    alCambiarEstado,
    size = 'xs'
}) => {
    const acciones = transiciones.map(estado => accionTransicionEstado(estadoId, estado));
    const editable = acciones.length > 0 && !!alCambiarEstado;
    const Icono = (estadoId != null && ICONOS_ESTADO[estadoId]) || IconPencil;

    const chip = (
        <Chip
            checked
            readOnly
            variant="filled"
            size={size}
            color={colorEstadoCotizacion(estadoId)}
            icon={<Icono size={12} />}
            // Chip es un checkbox (input + label): el clic en la label dispara un segundo clic en el
            // input y el menú se abría y cerraba a la vez. El chip no recibe clics; los toma el contenedor.
            style={{ textTransform: 'none', pointerEvents: 'none' }}
        >
            {nombreEstadoCotizacion(estadoId)}
            {editable && <IconChevronDown size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />}
        </Chip>
    );

    if (!editable) return chip;

    return (
        <Menu position="bottom-start" withinPortal shadow="md" width={170}>
            <Menu.Target>
                {/* El contenedor recibe el clic y la ref (el Chip reenvía la ref a su input oculto). */}
                <div role="button" tabIndex={0} aria-label="Cambiar estado" style={{ display: 'inline-block', cursor: 'pointer' }}>{chip}</div>
            </Menu.Target>
            <Menu.Dropdown p={4}>
                <Menu.Label fz={10} py={2}>Cambiar estado a</Menu.Label>
                {acciones.map(accion => {
                    const IconoDestino = ICONOS_ESTADO[accion.estado] ?? IconPencil;
                    return (
                        <Menu.Item
                            key={accion.estado}
                            fz="xs"
                            py={4}
                            leftSection={<IconoDestino size={12} color={accion.color} />}
                            onClick={() => alCambiarEstado!(accion.estado)}
                        >
                            {accion.etiqueta}
                        </Menu.Item>
                    );
                })}
            </Menu.Dropdown>
        </Menu>
    );
};
